import { useEffect, useId, useMemo, useState } from 'react';
import { Dialog } from '../ui/Dialog.js';

/**
 * R13 — the ⌘K command palette. A single fast jump to any reachable destination,
 * fed by the same destination registry the rail + keyboard use (so the names
 * match everywhere). Cross-queue navigation without reaching for the rail; it's
 * the answer to label-less icon rails at mid widths too.
 */
export interface Command {
  id: string;
  label: string;
  /** Optional right-aligned hint (e.g. a group name). */
  hint?: string;
  run: () => void;
}

/**
 * Subsequence fuzzy filter: every query char must appear in order in the label.
 * Empty query → all, in registry order. Pure, so the match logic is unit-tested.
 */
export function filterCommands(commands: Command[], rawQuery: string): Command[] {
  const q = rawQuery.trim().toLowerCase();
  if (q === '') return commands;
  return commands.filter((c) => isSubsequence(q, c.label.toLowerCase()));
}

function isSubsequence(needle: string, haystack: string): boolean {
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) i++;
  }
  return i === needle.length;
}

export function CommandPalette({
  open,
  onClose,
  commands,
}: {
  open: boolean;
  onClose: () => void;
  commands: Command[];
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();

  const filtered = useMemo(() => filterCommands(commands, query), [commands, query]);
  // Keep the active row inside the filtered range as it shrinks.
  const clampedActive = filtered.length === 0 ? -1 : Math.min(active, filtered.length - 1);

  // Reset to a clean slate each time it opens. (The component persists across
  // open/close — only the Dialog's children unmount — so the previous query
  // would otherwise linger.) The Dialog focus-trap moves focus to the first
  // focusable, which is the input (the option buttons are tabIndex=-1).
  // (Re-runs only on open; setQuery/setActive are stable.)
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
  }, [open]);

  // R13 review — keep the active row visible as it moves / wraps (long lists).
  // (Optional-call so jsdom, which has no scrollIntoView, is a no-op.)
  useEffect(() => {
    if (open && clampedActive >= 0) {
      document.getElementById(`${listId}-${clampedActive}`)?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [open, clampedActive, listId]);

  const runActive = () => {
    const cmd = filtered[clampedActive];
    if (cmd) {
      onClose();
      cmd.run();
    }
  };

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) =>
        filtered.length === 0 ? 0 : (Math.min(a, filtered.length - 1) + 1) % filtered.length,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => {
        if (filtered.length === 0) return 0;
        const cur = Math.min(a, filtered.length - 1);
        return (cur - 1 + filtered.length) % filtered.length;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runActive();
    }
    // Esc is the Dialog's job.
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      label="Command palette"
      testId="command-palette"
      className="command-palette"
    >
      {/* R13 review — combobox + aria-activedescendant: the input keeps focus, the
          option buttons are out of the tab cycle (tabIndex=-1). */}
      <input
        type="text"
        role="combobox"
        aria-expanded={filtered.length > 0}
        aria-autocomplete="list"
        aria-controls={filtered.length > 0 ? listId : undefined}
        aria-activedescendant={clampedActive >= 0 ? `${listId}-${clampedActive}` : undefined}
        data-testid="command-palette-input"
        className="command-palette-input"
        placeholder="Jump to…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onInputKeyDown}
      />
      {filtered.length === 0 ? (
        <p className="command-palette-empty" data-testid="command-palette-empty">
          No matches
        </p>
      ) : (
        <div id={listId} className="command-palette-list" role="listbox" aria-label="Commands">
          {filtered.map((c, i) => (
            <button
              key={c.id}
              type="button"
              tabIndex={-1}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === clampedActive}
              data-testid={`command-${c.id}`}
              className={
                i === clampedActive ? 'command-palette-row is-active' : 'command-palette-row'
              }
              onMouseEnter={() => setActive(i)}
              onClick={() => {
                onClose();
                c.run();
              }}
            >
              <span className="command-palette-label">{c.label}</span>
              {c.hint ? <span className="command-palette-hint">{c.hint}</span> : null}
            </button>
          ))}
        </div>
      )}
    </Dialog>
  );
}
